import { useState, useEffect } from "react";
import { trpc } from "../../trpc";
import SectionTitle from "../../components/SectionTitle";
import FunnelFilters from "../../components/funnel/FunnelFilters";
import FunnelTable from "../../components/funnel/FunnelTable";
import FunnelCard from "../../components/funnel/FunnelCard";
import { Spinner } from "@/components/ui/spinner";

type FunnelAnalyticsData = {
  merchantId: string;
  totalLeads: number;
  totalLeadsPercentage: number;
  totalDelivered: number;
  deliveredPercentage: number;
  totalRead: number;
  readPercentage: number;
  totalResponded: number;
  respondedPercentage: number;
  totalAppointmentIntention: number;
  appointmentIntentionPercentage: number;
  totalAppointments: number;
  appointmentsPercentage: number;
  totalTransfers: number;
  transfersPercentage: number;
  totalDiscarded: number;
  discardedPercentage: number;
  totalInactive: number;
  inactivePercentage: number;
};

type FunnelTotalData = Omit<FunnelAnalyticsData, "merchantId">;

const FunnelContainer = () => {
  const [merchantId, setMerchantId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [leadDirection, setLeadDirection] = useState<
    "inbound" | "inbound-email" | "outbound" | "pauta" | undefined
  >(undefined);
  const [projectId, setProjectId] = useState<string>("");

  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    setStartDate(firstDayOfMonth.toISOString().split("T")[0]);
    setEndDate(now.toISOString().split("T")[0]);
  }, []);

  const merchants = trpc.merchant.merchants.useQuery();

  const funnelQuery = trpc.funnel.leadFunnel.useQuery(
    {
      merchantId: merchantId || undefined,
      startDate,
      endDate,
      leadDirection,
      projectId: projectId || undefined,
    },
    {
      enabled: !!startDate && !!endDate,
    }
  );

  const funnelTotalQuery = trpc.funnel.leadFunnelTotal.useQuery(
    {
      merchantId: merchantId || undefined,
      startDate,
      endDate,
      leadDirection,
    },
    {
      enabled: !!startDate && !!endDate,
    }
  );

  const handleApplyFilters = () => {
    funnelQuery.refetch();
    funnelTotalQuery.refetch();
  };

  const funnelData = funnelQuery.data as FunnelAnalyticsData | FunnelAnalyticsData[] | undefined;
  const totalData = funnelTotalQuery.data as FunnelTotalData | undefined;

  return (
    <div className="p-6 mx-auto w-full">
      <div className="flex flex-col gap-6 w-full">
        <SectionTitle
          title="Funnel del Agente Comercial"
          description="Funnel de leads del pipeline default (mismo alcance que el dashboard del merchant)."
        />

        <FunnelFilters
          merchantId={merchantId}
          setMerchantId={setMerchantId}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          leadDirection={leadDirection}
          setLeadDirection={setLeadDirection}
          projectId={projectId}
          setProjectId={setProjectId}
          merchants={merchants.data}
          onApplyFilters={handleApplyFilters}
          isLoading={funnelQuery.isFetching || funnelTotalQuery.isFetching}
        />

        {funnelQuery.isFetching || funnelTotalQuery.isFetching ? (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        ) : funnelData || totalData ? (
          <div className="flex flex-col gap-6 w-full">
            {totalData && (
              <div className="grid grid-cols-1 gap-6 w-full">
                <FunnelCard data={totalData} isLoading={funnelTotalQuery.isFetching} colSpan={1} />
              </div>
            )}

            {funnelData && (
              <div className="w-full max-w-full overflow-hidden">
                <FunnelTable data={funnelData} />
              </div>
            )}
          </div>
        ) : startDate && endDate ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-gray-500">No data available for the selected filters</p>
          </div>
        ) : (
          <div className="flex items-center justify-center py-20">
            <p className="text-gray-500">Please select a date range to view the funnel</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FunnelContainer;
